import { useState } from 'react';
import './accident-data-review.scss';

export interface AccidentData {
  accident_type: string;
  user_name: string;
  car_model: string;
  description: string;
}

interface AccidentDataReviewProps {
  accidentData: AccidentData;
  location?: string;
  imageCount?: number;
  onConfirm: (data: AccidentData) => void;
  onCancel: () => void;
  language?: 'ar' | 'en' | 'ur';
}

export default function AccidentDataReview({
  accidentData,
  location,
  imageCount = 0,
  onConfirm,
  onCancel,
  language = 'ar',
}: AccidentDataReviewProps) {
  const [accidentType, setAccidentType] = useState(accidentData.accident_type || 'minor_accident');
  const [userName, setUserName] = useState(accidentData.user_name || '');
  const [carModel, setCarModel] = useState(accidentData.car_model || '');
  const [description, setDescription] = useState(accidentData.description || '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const texts = {
    ar: {
      title: 'مراجعة بيانات البلاغ',
      subtitle: 'يرجى مراجعة البيانات وتعديلها إذا لزم الأمر',
      accidentType: 'نوع الحادث',
      minorAccident: 'حادث بسيط',
      hitAndRun: 'صدمة وهروب',
      unknownParty: 'طرف مجهول',
      userName: 'الاسم',
      userNamePlaceholder: 'الاسم الكامل',
      carModel: 'موديل السيارة',
      carModelPlaceholder: 'مثال: تويوتا كامري 2022',
      description: 'وصف الحادث',
      descriptionPlaceholder: 'وصف تفصيلي للحادث والأضرار',
      location: 'الموقع',
      images: 'عدد الصور',
      confirm: 'تأكيد وإرسال البلاغ',
      cancel: 'إلغاء',
      requiredError: 'هذا الحقل مطلوب',
      note: 'تأكد من صحة البيانات قبل الإرسال',
      notAvailable: 'غير متاح',
    },
    en: {
      title: 'Review Report Data',
      subtitle: 'Please review and edit the data if necessary',
      accidentType: 'Accident Type',
      minorAccident: 'Minor Accident',
      hitAndRun: 'Hit and Run',
      unknownParty: 'Unknown Party',
      userName: 'Name',
      userNamePlaceholder: 'Full name',
      carModel: 'Car Model',
      carModelPlaceholder: 'Example: Toyota Camry 2022',
      description: 'Accident Description',
      descriptionPlaceholder: 'Detailed description of the accident and damage',
      location: 'Location',
      images: 'Number of Images',
      confirm: 'Confirm & Submit Report',
      cancel: 'Cancel',
      requiredError: 'This field is required',
      note: 'Make sure the data is correct before submitting',
      notAvailable: 'Not available',
    },
    ur: {
      title: 'رپورٹ ڈیٹا کا جائزہ',
      subtitle: 'براہ کرم ڈیٹا کا جائزہ لیں اور ضرورت ہو تو ترمیم کریں',
      accidentType: 'حادثے کی قسم',
      minorAccident: 'معمولی حادثہ',
      hitAndRun: 'ٹکر مار کر فرار',
      unknownParty: 'نامعلوم فریق',
      userName: 'نام',
      userNamePlaceholder: 'پورا نام',
      carModel: 'گاڑی کا ماڈل',
      carModelPlaceholder: 'مثال: ٹویوٹا کیمری 2022',
      description: 'حادثے کی تفصیل',
      descriptionPlaceholder: 'حادثے اور نقصان کی تفصیلی وضاحت',
      location: 'مقام',
      images: 'تصاویر کی تعداد',
      confirm: 'تصدیق کریں اور رپورٹ جمع کرائیں',
      cancel: 'منسوخ',
      requiredError: 'یہ فیلڈ ضروری ہے',
      note: 'جمع کرانے سے پہلے ڈیٹا کی درستگی کو یقینی بنائیں',
      notAvailable: 'دستیاب نہیں',
    },
  };

  const t = texts[language];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!userName.trim()) newErrors.userName = t.requiredError;
    if (!carModel.trim()) newErrors.carModel = t.requiredError;
    if (!description.trim()) newErrors.description = t.requiredError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validate()) {
      onConfirm({
        accident_type: accidentType,
        user_name: userName.trim(),
        car_model: carModel.trim(),
        description: description.trim(),
      });
    }
  };

  return (
    <div className="accident-data-review-container">
      <div className="review-header">
        <h3>{t.title}</h3>
        <p className="subtitle">{t.subtitle}</p>
      </div>

      <div className="form-container">
        {/* Accident Type */}
        <div className="form-field">
          <label htmlFor="accidentType">{t.accidentType}</label>
          <select
            id="accidentType"
            value={accidentType}
            onChange={(e) => setAccidentType(e.target.value)}
          >
            <option value="minor_accident">{t.minorAccident}</option>
            <option value="hit_and_run">{t.hitAndRun}</option>
            <option value="unknown_party">{t.unknownParty}</option>
          </select>
        </div>

        {/* User Name */}
        <div className="form-field">
          <label htmlFor="userName">
            {t.userName}
            <span className="required">*</span>
          </label>
          <input
            id="userName"
            type="text"
            value={userName}
            onChange={(e) => {
              setUserName(e.target.value);
              if (errors.userName) setErrors({ ...errors, userName: '' });
            }}
            placeholder={t.userNamePlaceholder}
            className={errors.userName ? 'error' : ''}
          />
          {errors.userName && (
            <span className="error-message">
              <span className="material-symbols-outlined">error</span>
              {errors.userName}
            </span>
          )}
        </div>

        {/* Car Model */}
        <div className="form-field">
          <label htmlFor="carModel">
            {t.carModel}
            <span className="required">*</span>
          </label>
          <input
            id="carModel"
            type="text"
            value={carModel}
            onChange={(e) => {
              setCarModel(e.target.value);
              if (errors.carModel) setErrors({ ...errors, carModel: '' });
            }}
            placeholder={t.carModelPlaceholder}
            className={errors.carModel ? 'error' : ''}
          />
          {errors.carModel && (
            <span className="error-message">
              <span className="material-symbols-outlined">error</span>
              {errors.carModel}
            </span>
          )}
        </div>

        {/* Description (textarea — may contain multi-car vision output) */}
        <div className="form-field">
          <label htmlFor="description">
            {t.description}
            <span className="required">*</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (errors.description) setErrors({ ...errors, description: '' });
            }}
            placeholder={t.descriptionPlaceholder}
            className={errors.description ? 'error' : ''}
            rows={5}
          />
          {errors.description && (
            <span className="error-message">
              <span className="material-symbols-outlined">error</span>
              {errors.description}
            </span>
          )}
        </div>

        {/* Location — read-only info row */}
        <div className="form-field info-field">
          <label>{t.location}</label>
          <div className="info-value">
            <span className="material-symbols-outlined">location_on</span>
            <span>{location || t.notAvailable}</span>
          </div>
        </div>

        {/* Image count — read-only info row */}
        <div className="form-field info-field">
          <label>{t.images}</label>
          <div className="info-value">
            <span className="material-symbols-outlined">photo_library</span>
            <span>{imageCount}</span>
          </div>
        </div>
      </div>

      <div className="note-box">
        <span className="material-symbols-outlined">info</span>
        <span>{t.note}</span>
      </div>

      <div className="actions">
        <button className="confirm-button" onClick={handleConfirm}>
          <span className="material-symbols-outlined">send</span>
          {t.confirm}
        </button>
        <button className="cancel-button" onClick={onCancel}>
          {t.cancel}
        </button>
      </div>
    </div>
  );
}
